// FormPreviewFieldCard — one field as the MOBILE app renders it, inside the
// builder's phone-frame preview (FormPreviewOverlay). Deliberately its own
// renderer, not the canvas's FieldPreview: the mobile looks differ per type
// (checkList is option rows with a color dot, not chips; location is a map
// placeholder, not an input) and the design screenshots are the contract here.
// Controls are PICTURES of the mobile UI, not live form controls — drawn as
// inert spans/divs (the WelcomeScreenPreview idiom), since a real disabled
// Input would read greyed-out and break the device fiction. Read-only v1:
// nothing is fillable, and conditional follow-up (child) questions are NOT
// shown at all (user decision) — on mobile they only exist once their
// triggering answer is picked, which never happens in a read-only preview.

import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/common';
import type { BuilderField } from './utils/builderModel';
import type { CanvasFieldItem } from './utils/canvasItems';
import { splitHex8 } from './utils/builderColors';
import { RATING_PREVIEW_ICON } from './FormBuilderFieldCard';
import {
  basicRecord,
  type ChoiceOption,
  type FieldImageRef,
  type LibraryElementType,
} from './utils/formElementDefaults';
import { useFieldImageUrl } from './utils/useFieldImageUrl';

interface FormPreviewFieldCardProps {
  item: CanvasFieldItem;
  /** Position number on the page (restarts at 1 per page). */
  badge: number;
}

/** Media-allowance chips under the control — the mobile capture affordances.
 *  Capture and upload both surface as the one chip the field user would tap. */
const MEDIA_CHIPS: { keys: string[]; icon: string; labelKey: string }[] = [
  {
    keys: ['allowPictures', 'allowPictureUpload'],
    icon: 'camera',
    labelKey: 'forms_builder_preview_chip_photo',
  },
  {
    keys: ['allowVideos', 'allowVideoUpload'],
    icon: 'video',
    labelKey: 'forms_builder_preview_chip_video',
  },
  { keys: ['allowDocuments'], icon: 'file-text', labelKey: 'forms_builder_preview_chip_pdf' },
  { keys: ['allowNotes'], icon: 'notebook-pen', labelKey: 'forms_builder_preview_chip_note' },
];

/** A drawn (inert) input box. */
const MockInput = ({ icon, text }: { icon?: string; text: string }) => (
  <span className='form-preview__input'>
    {icon && <Icon name={icon} size={16} />}
    <span className='form-preview__input-ph'>{text}</span>
  </span>
);

/** A drawn bordered button-look row ("Pick a date", "Time"). */
const MockButton = ({ icon, text }: { icon: string; text: string }) => (
  <span className='form-preview__mock-btn'>
    <Icon name={icon} size={16} />
    {text}
  </span>
);

/** The "Pick a place" row shared by companyStructure and companyField. */
const PickRow = ({ icon }: { icon: string }) => {
  const { t } = useTranslation();
  return (
    <span className='form-preview__pick-row'>
      <Icon name={icon} size={16} />
      <span className='form-preview__pick-label'>{t('forms_builder_preview_pick_place')}</span>
      <span className='form-preview__chevron'>
        <Icon name='chevron-right' size={16} />
      </span>
    </span>
  );
};

const PreviewControl = ({ field, imageUrl }: { field: BuilderField; imageUrl?: string }) => {
  const { t } = useTranslation();
  const bp = basicRecord(field.properties);
  const placeholder = (bp.fieldPlaceholder as string) ?? '';

  switch (field.elementType as LibraryElementType) {
    case 'checkList': {
      const options = (bp.optionList as ChoiceOption[]) ?? [];
      if (!options.length) return null;
      return (
        <div className='form-preview__options'>
          {options.map((o, i) => {
            const { rgb, alpha } = splitHex8(o.itemColor);
            return (
              // eslint-disable-next-line react/no-array-index-key
              <span key={i} className='form-preview__option'>
                <span className='form-preview__option-radio' aria-hidden='true' />
                <span className='form-preview__option-label'>{o.responseText || '—'}</span>
                {alpha > 0 && (
                  <span
                    className='form-preview__option-dot'
                    style={{ backgroundColor: rgb }}
                    aria-hidden='true'
                  />
                )}
              </span>
            );
          })}
        </div>
      );
    }
    case 'dateTime': {
      const sub = bp.subType as string;
      return (
        <div className='form-preview__dt-row'>
          {sub !== 'time' && (
            <MockButton icon='calendar' text={t('forms_builder_preview_pick_date')} />
          )}
          {sub !== 'date' && <MockButton icon='clock' text={t('forms_builder_preview_time')} />}
        </div>
      );
    }
    case 'ratingSlider': {
      const count = Math.min(10, Math.max(1, (bp.ratingPointers as number) || 5));
      const iconName = RATING_PREVIEW_ICON[(bp.ratingIcon as string) ?? ''] ?? 'star';
      const low = (bp.lowLabel as string) ?? '';
      const high = (bp.highLabel as string) ?? '';
      return (
        <div className='form-preview__rating'>
          <div className='form-preview__rating-icons'>
            {Array.from({ length: count }, (_, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <Icon key={i} name={iconName} size={26} />
            ))}
          </div>
          {(low || high) && (
            <div className='form-preview__rating-ends'>
              <span>{low}</span>
              <span>{high}</span>
            </div>
          )}
        </div>
      );
    }
    case 'numberSlider': {
      const min = Number(bp.minRange ?? 0);
      const max = Number(bp.maxRange ?? 0);
      return (
        <div className='form-preview__slider-wrap'>
          <input
            type='range'
            className='form-preview__slider'
            min={min}
            max={max}
            defaultValue={min}
            disabled
          />
          <div className='form-preview__slider-caps'>
            <span>{min}</span>
            <span className='form-preview__slider-val'>{min}</span>
            <span>{max}</span>
          </div>
        </div>
      );
    }
    case 'image':
      // The reference image previews as the picture itself; otherwise the
      // capture placeholder the field user would tap.
      return imageUrl ? (
        <img className='form-preview__ref-img' src={imageUrl} alt='' />
      ) : (
        <span className='form-preview__pad'>
          <Icon name='camera' size={20} />
          {t('forms_builder_preview_photo')}
        </span>
      );
    case 'signature':
      return (
        <span className='form-preview__pad'>
          <Icon name='signature' size={20} />
          {t('forms_builder_preview_sign')}
        </span>
      );
    case 'location':
      return (
        <span className='form-preview__map'>
          <span className='form-preview__map-btn'>
            <Icon name='locate-fixed' size={16} />
            {t('forms_builder_preview_capture_location')}
          </span>
        </span>
      );
    case 'qrCode':
      return (
        <span className='form-preview__qr'>
          <Icon name='scan' size={18} />
          {t('forms_builder_preview_scan')}
        </span>
      );
    case 'otp':
      return (
        <div className='form-preview__otp'>
          <div className='form-preview__otp-boxes' aria-hidden='true'>
            {Array.from({ length: 4 }, (_, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <span key={i} className='form-preview__otp-box' />
            ))}
          </div>
          <span className='form-preview__otp-send'>{t('forms_builder_preview_send_code')}</span>
        </div>
      );
    case 'dataSource':
      return (
        <span className='form-preview__select-row'>
          <span className='form-preview__input-ph'>
            {placeholder || t('forms_builder_preview_select_option')}
          </span>
          <Icon name='chevron-down' size={16} />
        </span>
      );
    case 'sendTo':
      return <MockInput icon='mail' text={t('forms_builder_preview_email_ph')} />;
    case 'companyStructure':
      return <PickRow icon='building-2' />;
    case 'companyField':
      return <PickRow icon='building' />;
    case 'number':
      return <MockInput text={placeholder || '0'} />;
    // `section` never reaches the switch — the overlay's page walk renders
    // sections as group headings and recurses into their children.
    default:
      return <MockInput text={placeholder} />;
  }
};

const FormPreviewFieldCard = ({ item, badge }: FormPreviewFieldCardProps) => {
  const { t } = useTranslation();
  const { field } = item;
  const bp = basicRecord(field.properties);

  // Same untitled/required reads as the canvas card (.trim() so a whitespace-
  // only title falls back, matching the save-time validator).
  const ownTitle = ((bp.fieldTitle as string) ?? '').trim();
  const hint = ((bp.fieldHintText as string) ?? '').trim();
  const required =
    'validationProperties' in field.properties &&
    (field.properties.validationProperties as Record<string, unknown>).required === true;

  // Unconditional hook call — `skip` covers the non-image types (house rule,
  // see useFieldImageUrl's header).
  const imageUrl = useFieldImageUrl(
    bp.fieldImage as FieldImageRef | null | undefined,
    bp.fieldImageFile,
  );

  const mediaFlags =
    'mediaProperties' in field.properties
      ? (field.properties.mediaProperties as unknown as Record<string, unknown>)
      : undefined;
  const chips = mediaFlags
    ? MEDIA_CHIPS.filter((c) => c.keys.some((k) => mediaFlags[k] === true))
    : [];

  return (
    <div className='form-preview__card'>
      <div className='form-preview__card-head'>
        <span className='form-preview__badge'>{badge}</span>
        <div className='form-preview__card-text'>
          <span className='form-preview__label'>
            {ownTitle || t('forms_builder_untitled_field')}
            {required && (
              <span className='form-preview__req' aria-hidden='true'>
                *
              </span>
            )}
          </span>
          {hint && <span className='form-preview__desc'>{hint}</span>}
        </div>
      </div>

      <PreviewControl field={field} imageUrl={imageUrl} />

      {chips.length > 0 && (
        <div className='form-preview__media-chips'>
          {chips.map((c) => (
            <span key={c.icon} className='form-preview__media-chip'>
              <Icon name={c.icon} size={14} />
              {t(c.labelKey)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default FormPreviewFieldCard;
